import { Component, ElementRef, Input, OnChanges, SimpleChanges } from '@angular/core';
import {
  DEFAULT_DARK_THEME_BORDER_COLOR,
  DEFAULT_DARK_THEME_TEXT_COLOR,
  DEFAULT_LIGHT_THEME_BORDER_COLOR,
  DEFAULT_LIGHT_THEME_TEXT_COLOR,
  EVENTS_CONFIG,
  REPORT_INCIDENT_SUMMARY_CHART_OPTIONS,
} from '@app-core/constants/constants';
import { DataService } from '@app-core/services/data/data.service';
import { TranslateService } from '@ngx-translate/core';
import Chart from 'chart.js/auto';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import ChartDataLabels from 'chartjs-plugin-datalabels';

@Component({
  selector: 'app-report-incident-summary',
  templateUrl: './report-incident-summary.component.html',
  styleUrls: ['./report-incident-summary.component.scss'],
})
export class ReportIncidentSummaryComponent implements OnChanges {
  @Input()
  private eventDiff: any;
  @Input()
  private fleetStats: any;
  @Input()
  public loader = true;
  @Input()
  public currentTheme = 'light';
  public showChart = false;
  public ngUnsubscribe = new Subject<void>();
  public currentLanguage: string;
  public chart: Chart;
  public dataSeries = [];
  public chartLabels: any;
  public combinedEventsList;
  public modifiedEventsConfig;

  constructor(public translate: TranslateService, public dataService: DataService, private elementRef: ElementRef) {}

  public ngOnChanges(changes: SimpleChanges) {
    if (changes.currentTheme) {
      this.changeTheme();
    }
    Chart.register(ChartDataLabels);
    const { total: { current: _totalIncidentCount = 0 } = {}, ...otherEventsDiff } = this.eventDiff || ({} as any);
    if (this.fleetStats) {
      const { aggregate } = this.fleetStats || {};
      const { tripCount } = aggregate;
      this.showChart = !!tripCount;
    }
    const eventsArray = [];
    for (let key in this.eventDiff) {
      eventsArray.push({
        key,
        count: this.eventDiff[key].current,
        difference: this.eventDiff[key].diff,
      });
    }
    this.combinedEventsList = this.dataService.modifyFleeEvents(true);
    this.modifiedEventsConfig = { ...EVENTS_CONFIG, ...this.dataService.transformObject(this.combinedEventsList) };
    const eventsToShow = Object.keys({ ...EVENTS_CONFIG, ...this.modifiedEventsConfig });
    const updatedEventsToShow = eventsToShow.filter((e) => e !== 'Lane-Drift-Found');
    this.dataService._currentLanguage.pipe(takeUntil(this.ngUnsubscribe)).subscribe((value: string) => {
      if (value) {
        this.currentLanguage = value;
        this.translate.stream('incidentBarGraph').subscribe((text: string) => {
          this.showData(otherEventsDiff, updatedEventsToShow, text);
        });
      }
    });
  }

  public changeTheme() {
    if (this.chart) {
      if (this.currentTheme === 'light') {
        this.chart.options.plugins.legend.labels.color = DEFAULT_LIGHT_THEME_TEXT_COLOR;
        this.chart.options.scales.y.grid.color = DEFAULT_LIGHT_THEME_BORDER_COLOR;
        this.chart.options.scales.x.ticks.color = DEFAULT_LIGHT_THEME_TEXT_COLOR;
        this.chart.options.scales.y.ticks.color = DEFAULT_LIGHT_THEME_TEXT_COLOR;
        this.chart.options.plugins.datalabels.color = DEFAULT_LIGHT_THEME_TEXT_COLOR;
      } else {
        this.chart.options.plugins.legend.labels.color = DEFAULT_DARK_THEME_TEXT_COLOR;
        this.chart.options.scales.y.grid.color = DEFAULT_DARK_THEME_BORDER_COLOR;
        this.chart.options.scales.x.ticks.color = DEFAULT_DARK_THEME_TEXT_COLOR;
        this.chart.options.scales.y.ticks.color = DEFAULT_DARK_THEME_TEXT_COLOR;
        this.chart.options.plugins.datalabels.color = DEFAULT_DARK_THEME_TEXT_COLOR;
      }
      this.chart.update();
    }
  }

  public showData(otherEventsDiff: any, eventsToShow: any, text) {
    this.dataSeries = Object.entries(otherEventsDiff)
      .filter(([eventType]: [string, any]) => eventsToShow.includes(eventType))
      .map(([eventType, value]: [string, any]) => {
        return {
          name: this.modifiedEventsConfig[eventType].label,
          y: value.current,
          count: value.current,
          diffCount: value.diff,
          diffPercentage: value.percent.toFixed(2),
          label: this.modifiedEventsConfig[eventType].label,
          data: value.current,
        };
      })
      .sort((a, b) => (b.y - a.y > 0 ? 1 : -1));

    if (this.dataSeries.length) {
      if (this.chart) {
        this.chart.destroy();
      }
      this.dataSeries.map((seriesDataKey, seriesindex) => {
        if (text.barMapData) {
          text.barMapData.map((languageDataKey, languageIndex) => {
            if (seriesDataKey.name === languageDataKey.name) {
              this.dataSeries[seriesindex].name = text.barMapData[languageIndex].Key;
            }
          });
        }
      });
      const ctx: any = this.elementRef.nativeElement.querySelector(`#reportIncidentSummaryChart`);
      const chartStatus = Chart.getChart('reportIncidentSummaryChart');
      if (chartStatus != undefined) {
        chartStatus.destroy();
      }
      let chartData = {
        ...REPORT_INCIDENT_SUMMARY_CHART_OPTIONS,
      };
      chartData.options.scales.y.title = {
        ...chartData.options.scales.y.title,
        text: `${text.incidentBarGraphYAxis}`,
      };
      const chart = new Chart(ctx, {
        ...chartData,
        data: {
          labels: this.dataSeries.map((x) => x.name),
          datasets: [
            {
              label: `${text.incidentBarGraphTotalToolip}`,
              data: this.dataSeries.map((x) => x.data),
              barPercentage: 0.5,
              backgroundColor: '#7BB4EC',
              diffPercentage: this.dataSeries.map((x) => x.diffPercentage),
            },
          ],
        },
      } as any);

      this.chart = chart;

      if (this.currentLanguage !== 'en') {
        this.chart.options.plugins.datalabels.formatter = (_data: any, context: any) => {
          const chars = { ',': '.', '.': ',' };
          let diff;
          if (context.dataset.diffPercentage[context.dataIndex] && context.dataset.diffPercentage[context.dataIndex] !== '0.00') {
            diff = context.dataset.diffPercentage[context.dataIndex].toString().replace(/[,.]/g, (m) => chars[m]);
          } else {
            diff = ``;
          }
          return diff;
        };
        this.chart.options.plugins.tooltip.callbacks.afterBody = (data: any) => {
          const { dataset = [], dataIndex = 0 } = data[0] || {};
          const { diffPercentage = [] } = dataset;
          const chars = { ',': '.', '.': ',' };
          const diff = diffPercentage[dataIndex] ? diffPercentage[dataIndex].toString().replace(/[,.]/g, (m) => chars[m]) : 0;
          return [`${text.incidentBarGraphDiffereceToolip}: ${diff}%`];
        };
        this.chart.update();
      } else {
        this.chart.options.plugins.tooltip.callbacks.afterBody = (data: any) => {
          const { dataset = [], dataIndex = 0 } = data[0] || {};
          const { diffPercentage = [] } = dataset;
          return [`${text.incidentBarGraphDiffereceToolip}: ${diffPercentage[dataIndex]}%`];
        };
        this.chart.options.plugins.datalabels.formatter = (_data: any, context: any) => {
          if (context.dataset.diffPercentage[context.dataIndex] !== '0.00') {
            return `${context.dataset.diffPercentage[context.dataIndex]}%`;
          } else {
            return ``;
          }
        };
      }

      this.changeTheme();
      this.chart.update();
    }
  }
}
